import React, { useState, useEffect } from 'react';
import {
  Lock, Plus, Trash2, Search, AlertTriangle,
  Calendar, Shield, Ban, Eye, Clock, Pencil, Settings2
} from 'lucide-react';
import MemberSearchSelect from './MemberSearchSelect';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Checkbox } from '../../components/ui/checkbox';
import { useAdminAuth } from '../context/AdminAuthContext';
import { toast } from 'sonner';
import { getErrorMessage } from '../../lib/utils';

const ReasonConfigSection = ({ label, reasons, onAdd, onRemove }) => {
  const [newReason, setNewReason] = useState('');
  return (
    <div className="border rounded-lg p-4 bg-gray-50">
      <h4 className="font-medium text-gray-900 mb-3">{label}</h4>
      <div className="space-y-2">
        {reasons.map((r, idx) => (
          <div key={idx} className="flex items-center justify-between bg-white px-3 py-2 rounded border">
            <span className="text-sm">{r}</span>
            <Button
              size="sm"
              variant="ghost"
              className="text-red-600 hover:text-red-700 hover:bg-red-50 h-8 w-8 p-0"
              onClick={() => onRemove(idx)}
            >
              <Trash2 size={14} />
            </Button>
          </div>
        ))}
        <div className="flex gap-2">
          <Input
            placeholder="Add new reason..."
            value={newReason}
            onChange={(e) => setNewReason(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                if (newReason.trim()) {
                  onAdd(newReason.trim());
                  setNewReason('');
                }
              }
            }}
            className="flex-1"
          />
          <Button
            size="sm"
            variant="outline"
            onClick={() => {
              if (newReason.trim()) {
                onAdd(newReason.trim());
                setNewReason('');
              }
            }}
          >
            Add
          </Button>
        </div>
      </div>
    </div>
  );
};

const RESTRICTION_TYPE_OPTIONS = [
  { value: 'full', label: 'Full Access Block' },
  { value: 'quotes', label: 'Quote Tool Block' },
  { value: 'events', label: 'Events Block' },
  { value: 'chat', label: 'Chat Block' },
  { value: 'ma', label: 'M&A Block' },
  { value: 'directory', label: 'Hide from Directory' },
];

const AdminRestrictions = () => {
  const [restrictions, setRestrictions] = useState([]);
  const [reasonOptions, setReasonOptions] = useState({});
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showReasonsConfig, setShowReasonsConfig] = useState(false);
  const [reasonsConfig, setReasonsConfig] = useState({});
  const [reasonsConfigSaving, setReasonsConfigSaving] = useState(false);
  const [editingRestriction, setEditingRestriction] = useState(null);
  const [membersList, setMembersList] = useState([]);
  const [membersLoading, setMembersLoading] = useState(false);
  const [form, setForm] = useState({
    member_id: '',
    restriction_types: ['full'],
    reason: '',
    expires_at: '',
    send_email: true
  });

  const { api, hasPermission } = useAdminAuth();

  useEffect(() => {
    fetchRestrictions();
  }, []);

  const fetchReasonOptions = async () => {
    if (!hasPermission('members', 'restrict')) return;
    try {
      const res = await api.get('/api/admin/restrictions/reason-options');
      const opts = res.data?.reason_options || {};
      setReasonOptions(opts);
      setReasonsConfig(JSON.parse(JSON.stringify(opts)));
    } catch (e) {
      const fallback = {
        full: ['Terms violation', 'Payment dispute', 'Security concern', 'Account abuse', 'Other'],
        quotes: ['Spam/abuse', 'Fraudulent activity', 'Policy violation', 'Other'],
        events: ['No-show history', 'Policy violation', 'Other'],
        chat: ['Inappropriate messaging', 'Spam', 'Harassment', 'Other'],
        ma: ['Misuse of listings', 'Policy violation', 'Other'],
        directory: ['Privacy request', 'Profile violation', 'Other'],
      };
      setReasonOptions(fallback);
      setReasonsConfig(JSON.parse(JSON.stringify(fallback)));
    }
  };

  useEffect(() => {
    fetchReasonOptions();
  }, [api, hasPermission]);

  useEffect(() => {
    if (showReasonsConfig && Object.keys(reasonOptions).length > 0) {
      setReasonsConfig(JSON.parse(JSON.stringify(reasonOptions)));
    }
  }, [showReasonsConfig, reasonOptions]);

  useEffect(() => {
    const fetchMembers = async () => {
      if (!showAddDialog || !hasPermission('members', 'restrict')) return;
      try {
        setMembersLoading(true);
        const res = await api.get('/api/admin/members/?limit=500');
        setMembersList(res.data?.members || []);
      } catch (e) {
        toast.error('Failed to load members');
        setMembersList([]);
      } finally {
        setMembersLoading(false);
      }
    };
    fetchMembers();
  }, [showAddDialog, api, hasPermission]);

  const fetchRestrictions = async () => {
    try {
      setLoading(true);
      const response = await api.get('/api/admin/restrictions');
      setRestrictions(response.data.restrictions || []);
    } catch (error) {
      toast.error('Failed to load restrictions');
    } finally {
      setLoading(false);
    }
  };

  const getRestrictionTypes = (r) => {
    return r.restriction_types || (r.restriction_type ? [r.restriction_type] : []);
  };

  const getReasonOptionsForTypes = (types) => {
    const seen = new Set();
    const options = [];
    for (const t of (types || [])) {
      const opts = reasonOptions[t] || [];
      for (const o of opts) {
        if (!seen.has(o)) {
          seen.add(o);
          options.push(o);
        }
      }
    }
    const list = options.length ? options : ['Other'];
    // Always show "Other" last (case-insensitive)
    return [...list.filter((o) => o.toLowerCase() !== 'other'), ...list.filter((o) => o.toLowerCase() === 'other')];
  };

  const handleAddRestriction = async () => {
    const types = Array.isArray(form.restriction_types) ? form.restriction_types : [form.restriction_types];
    if (!form.member_id || types.length === 0 || !form.reason) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      await api.post('/api/admin/restrictions', {
        member_id: form.member_id,
        restriction_types: types,
        reason: form.reason,
        expires_at: form.expires_at || null,
        send_email: form.send_email !== false
      });
      toast.success('Restriction added');
      setShowAddDialog(false);
      resetForm();
      fetchRestrictions();
    } catch (error) {
      toast.error(getErrorMessage(error.response?.data?.detail, 'Failed to add restriction'));
    }
  };

  const handleUpdateRestriction = async () => {
    const types = Array.isArray(form.restriction_types) ? form.restriction_types : [form.restriction_types];
    if (!editingRestriction || types.length === 0 || !form.reason) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      await api.put(`/api/admin/restrictions/${editingRestriction.restriction_id}`, {
        restriction_types: types,
        reason: form.reason,
        expires_at: form.expires_at || null,
        send_email: form.send_email !== false
      });
      toast.success('Restriction updated');
      setShowEditDialog(false);
      setEditingRestriction(null);
      resetForm();
      fetchRestrictions();
    } catch (error) {
      toast.error(getErrorMessage(error.response?.data?.detail, 'Failed to update restriction'));
    }
  };

  const handleRemoveRestriction = async (restrictionId) => {
    if (!window.confirm('Are you sure you want to remove this restriction?')) return;

    try {
      await api.delete(`/api/admin/restrictions/${restrictionId}`);
      toast.success('Restriction removed');
      fetchRestrictions();
    } catch (error) {
      toast.error(getErrorMessage(error.response?.data?.detail, 'Failed to remove restriction'));
    }
  };

  const openEditDialog = (restriction) => {
    const types = getRestrictionTypes(restriction);
    setEditingRestriction(restriction);
    setForm({
      member_id: restriction.member_id,
      restriction_types: types,
      reason: restriction.reason || '',
      expires_at: restriction.expires_at ? String(restriction.expires_at).slice(0, 10) : '',
      send_email: true
    });
    setShowEditDialog(true);
  };

  const resetForm = () => {
    setForm({
      member_id: '',
      restriction_types: ['full'],
      reason: '',
      expires_at: '',
      send_email: true
    });
  };

  const addReasonToConfig = (restrictionType, newReason) => {
    const trimmed = (newReason || '').trim();
    if (!trimmed) return;
    const list = [...(reasonsConfig[restrictionType] || []), trimmed];
    setReasonsConfig({ ...reasonsConfig, [restrictionType]: list });
  };

  const removeReasonFromConfig = (restrictionType, index) => {
    const list = [...(reasonsConfig[restrictionType] || [])];
    list.splice(index, 1);
    setReasonsConfig({ ...reasonsConfig, [restrictionType]: list });
  };

  const saveReasonsConfig = async () => {
    try {
      setReasonsConfigSaving(true);
      // Ensure we send a complete structure with all restriction types (backend expects reason_options)
      const payload = RESTRICTION_TYPE_OPTIONS.reduce((acc, opt) => {
        acc[opt.value] = Array.isArray(reasonsConfig[opt.value]) ? reasonsConfig[opt.value] : [];
        return acc;
      }, {});
      await api.put('/api/admin/restrictions/reason-options', { reason_options: payload }, {
        headers: { 'Content-Type': 'application/json' }
      });
      setReasonOptions(payload);
      toast.success('Restriction reasons updated');
      setShowReasonsConfig(false);
    } catch (e) {
      toast.error(getErrorMessage(e.response?.data?.detail, 'Failed to save'));
    } finally {
      setReasonsConfigSaving(false);
    }
  };

  const toggleRestrictionType = (value) => {
    const current = Array.isArray(form.restriction_types) ? form.restriction_types : [form.restriction_types];
    let next;
    if (value === 'full') {
      next = current.includes('full') ? [] : ['full'];
    } else {
      next = current.includes(value)
        ? current.filter((t) => t !== value)
        : [...current.filter((t) => t !== 'full'), value];
    }
    setForm({ ...form, restriction_types: next });
  };

  const getRestrictionBadge = (type) => {
    const badges = {
      full: { bg: 'bg-red-100', text: 'text-red-700', label: 'Full Access Blocked' },
      quotes: { bg: 'bg-orange-100', text: 'text-orange-700', label: 'Quote Tool Blocked' },
      events: { bg: 'bg-purple-100', text: 'text-purple-700', label: 'Events Blocked' },
      chat: { bg: 'bg-blue-100', text: 'text-blue-700', label: 'Chat Blocked' },
      ma: { bg: 'bg-amber-100', text: 'text-amber-700', label: 'M&A Blocked' },
      directory: { bg: 'bg-gray-100', text: 'text-gray-700', label: 'Directory Hidden' }
    };
    return badges[type] || badges.full;
  };

  const getRestrictionIcon = (type) => {
    const icons = {
      full: <Ban size={16} className="text-red-500" />,
      quotes: <AlertTriangle size={16} className="text-orange-500" />,
      events: <Calendar size={16} className="text-purple-500" />,
      chat: <Lock size={16} className="text-blue-500" />,
      ma: <Shield size={16} className="text-amber-500" />,
      directory: <Eye size={16} className="text-gray-500" />
    };
    return icons[type] || icons.full;
  };

  const filteredRestrictions = restrictions.filter(r => {
    if (!search) return true;
    const types = getRestrictionTypes(r);
    return r.member_id?.toLowerCase().includes(search.toLowerCase()) ||
      r.member_name?.toLowerCase().includes(search.toLowerCase()) ||
      r.company_name?.toLowerCase().includes(search.toLowerCase()) ||
      r.reason?.toLowerCase().includes(search.toLowerCase()) ||
      types.some(t => t?.toLowerCase().includes(search.toLowerCase()));
  });

  const fullBlockCount = restrictions.filter(r => getRestrictionTypes(r).includes('full')).length;
  const featureBlockCount = restrictions.filter(r => getRestrictionTypes(r).some(t => t !== 'full')).length;

  const RestrictionForm = ({ onSave, onCancel, isEdit }) => (
    <div className="space-y-4">
      {!isEdit && (
        <div className="min-w-0">
          <MemberSearchSelect
            members={membersList}
            value={form.member_id}
            onChange={(member_id) => setForm((f) => ({ ...f, member_id }))}
            disabled={membersLoading}
          />
          {membersLoading && (
            <p className="text-xs text-gray-500 mt-1">Loading members...</p>
          )}
        </div>
      )}

      <div className="min-w-0">
        <Label>Restriction Type *</Label>
        <div className="mt-2 space-y-2 border rounded-md p-3 bg-gray-50">
          {RESTRICTION_TYPE_OPTIONS.map((opt) => {
            const types = form.restriction_types || [];
            const isFullSelected = types.includes('full');
            const isDisabled = isFullSelected && opt.value !== 'full';
            return (
              <div key={opt.value} className={`flex items-center space-x-2 ${isDisabled ? 'opacity-60' : ''}`}>
                <Checkbox
                  id={`restriction-${opt.value}`}
                  checked={types.includes(opt.value)}
                  onCheckedChange={() => toggleRestrictionType(opt.value)}
                  disabled={isDisabled}
                />
                <label
                  htmlFor={`restriction-${opt.value}`}
                  className={`text-sm ${isDisabled ? 'cursor-not-allowed' : 'cursor-pointer'}`}
                >
                  {opt.label}
                </label>
              </div>
            );
          })}
        </div>
      </div>

      <div className="min-w-0">
        <Label>Reason *</Label>
        <select
          value={form.reason}
          onChange={(e) => setForm({ ...form, reason: e.target.value })}
          className="w-full max-w-full mt-1 px-3 py-2 border rounded-md"
        >
          <option value="">Select a reason...</option>
          {getReasonOptionsForTypes(form.restriction_types).map((opt) => (
            <option key={opt} value={opt}>{opt}</option>
          ))}
        </select>
      </div>

      <div className="min-w-0">
        <Label>Expiry Date (optional)</Label>
        <Input
          type="date"
          value={form.expires_at}
          onChange={(e) => setForm({ ...form, expires_at: e.target.value })}
          className="mt-1 w-full max-w-full"
        />
        <p className="text-xs text-gray-500 mt-1">Leave empty for permanent restriction</p>
      </div>

      {!isEdit && (
        <label className="flex items-center gap-2 cursor-pointer">
          <Checkbox
            checked={form.send_email}
            onCheckedChange={(checked) => setForm({ ...form, send_email: !!checked })}
          />
          <span className="text-sm">Send email notification to member</span>
        </label>
      )}
      {isEdit && (
        <label className="flex items-center gap-2 cursor-pointer">
          <Checkbox
            checked={form.send_email}
            onCheckedChange={(checked) => setForm({ ...form, send_email: !!checked })}
          />
          <span className="text-sm">Send email notification about this update</span>
        </label>
      )}

      <div className="p-3 bg-amber-50 rounded-lg text-sm text-amber-700">
        <AlertTriangle size={16} className="inline mr-2" />
        This action will immediately affect the member&apos;s access to the platform.
      </div>

      <div className="flex justify-end space-x-2 pt-4 border-t mt-4">
        <Button variant="outline" onClick={onCancel}>Cancel</Button>
        <Button onClick={onSave} className="bg-red-600 hover:bg-red-700 text-white">
          <Lock size={16} className="mr-2" />
          Apply Restriction
        </Button>
      </div>
    </div>
  );

  return (
    <div className="space-y-6" data-testid="admin-restrictions">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Member Restrictions</h1>
          <p className="text-gray-500">Manage member access restrictions and limitations</p>
        </div>
        {hasPermission('members', 'restrict') && (
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => setShowReasonsConfig(true)}
            >
              <Settings2 size={16} className="mr-2" />
              Configure Reasons
            </Button>
            <Button
              onClick={() => { resetForm(); setShowAddDialog(true); }}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="add-restriction-btn"
            >
              <Plus size={16} className="mr-2" />
              Add Restriction
            </Button>
          </div>
        )}
      </div>

      <div className="grid grid-cols-4 gap-4">
        <Card className="bg-gradient-to-br from-red-500 to-red-600 text-white">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-red-100">Total Active</p>
                <p className="text-3xl font-bold">{restrictions.length}</p>
              </div>
              <Ban size={32} className="text-red-200" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Full Block</p>
                <p className="text-2xl font-bold">{fullBlockCount}</p>
              </div>
              <Ban size={24} className="text-red-500" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Feature Blocks</p>
                <p className="text-2xl font-bold">{featureBlockCount}</p>
              </div>
              <Lock size={24} className="text-orange-500" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Expiring Soon</p>
                <p className="text-2xl font-bold">
                  {restrictions.filter(r => {
                    if (!r.expires_at) return false;
                    const expiry = new Date(r.expires_at);
                    const week = new Date();
                    week.setDate(week.getDate() + 7);
                    return expiry <= week;
                  }).length}
                </p>
              </div>
              <Clock size={24} className="text-yellow-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardContent className="p-4">
          <div className="relative">
            <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <Input
              placeholder="Search by member ID or reason..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10"
            />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-8 text-center text-gray-500">Loading restrictions...</div>
          ) : filteredRestrictions.length === 0 ? (
            <div className="p-8 text-center">
              <Shield size={48} className="mx-auto mb-4 text-gray-300" />
              <p className="text-gray-500">No active restrictions</p>
            </div>
          ) : (
            <table className="w-full">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Member</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Restriction Type</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Reason</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Applied</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Expires</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {filteredRestrictions.map((restriction) => {
                  const types = getRestrictionTypes(restriction);
                  const memberName = (restriction.member_name || '').trim();
                  const companyName = (restriction.company_name || '').trim();
                  const primaryLabel = memberName || companyName || restriction.member_id;
                  const secondaryLabel = memberName && companyName ? companyName : restriction.member_id;
                  const initial = (primaryLabel || 'M').charAt(0).toUpperCase();
                  return (
                    <tr key={restriction.restriction_id} className="hover:bg-gray-50">
                      <td className="px-4 py-3">
                        <div className="flex items-center space-x-3">
                          <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                            <span className="text-sm font-semibold text-blue-700">{initial}</span>
                          </div>
                          <div className="min-w-0">
                            <p className="text-base font-medium text-gray-900 truncate" title={primaryLabel}>{primaryLabel}</p>
                            <p className="text-sm text-gray-500 truncate" title={secondaryLabel}>{secondaryLabel}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex flex-wrap gap-1">
                          {types.map((t) => {
                            const badge = getRestrictionBadge(t);
                            return (
                              <Badge key={t} className={`${badge.bg} ${badge.text} flex items-center gap-1 w-fit`}>
                                {getRestrictionIcon(t)}
                                {badge.label}
                              </Badge>
                            );
                          })}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <p className="text-sm max-w-xs truncate" title={restriction.reason}>
                          {restriction.reason}
                        </p>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-500">
                        {restriction.created_at ? new Date(restriction.created_at).toLocaleDateString() : 'N/A'}
                      </td>
                      <td className="px-4 py-3">
                        {restriction.expires_at ? (
                          <span className="text-sm">{new Date(restriction.expires_at).toLocaleDateString()}</span>
                        ) : (
                          <Badge variant="outline" className="text-red-600 border-red-300">Permanent</Badge>
                        )}
                      </td>
                      <td className="px-4 py-3 text-right">
                        {hasPermission('members', 'restrict') && (
                          <div className="flex justify-end gap-1">
                            <Button
                              size="sm"
                              variant="outline"
                              className="text-blue-600 border-blue-300 hover:bg-blue-50"
                              onClick={() => openEditDialog(restriction)}
                            >
                              <Pencil size={14} className="mr-1" />
                              Edit
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              className="text-red-600 border-red-300 hover:bg-red-50"
                              onClick={() => handleRemoveRestriction(restriction.restriction_id)}
                            >
                              <Trash2 size={14} className="mr-1" />
                              Remove
                            </Button>
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>

      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto p-6 w-full [scrollbar-gutter:stable]">
          <DialogHeader>
            <DialogTitle className="flex items-center text-red-600">
              <Ban size={20} className="mr-2" />
              Add Member Restriction
            </DialogTitle>
          </DialogHeader>
          <div className="mt-4">
            <RestrictionForm onSave={handleAddRestriction} onCancel={() => setShowAddDialog(false)} isEdit={false} />
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto p-6 w-full [scrollbar-gutter:stable]">
          <DialogHeader>
            <DialogTitle className="flex items-center text-red-600">
              <Pencil size={20} className="mr-2" />
              Manage Restriction
            </DialogTitle>
          </DialogHeader>
          <div className="mt-4">
            <RestrictionForm onSave={handleUpdateRestriction} onCancel={() => { setShowEditDialog(false); setEditingRestriction(null); }} isEdit={true} />
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={showReasonsConfig} onOpenChange={setShowReasonsConfig}>
        <DialogContent className="max-h-[90vh] flex flex-col overflow-hidden p-6 max-w-2xl">
          <DialogHeader className="shrink-0">
            <DialogTitle className="flex items-center">
              <Settings2 size={20} className="mr-2" />
              Configure Restriction Reasons
            </DialogTitle>
            <p className="text-sm text-gray-500 mt-1">Add or remove reason options for each restriction type. These appear in the Reason dropdown when adding/editing restrictions.</p>
          </DialogHeader>
          <div className="overflow-y-auto flex-1 space-y-6 pr-2">
            {RESTRICTION_TYPE_OPTIONS.map((opt) => (
              <ReasonConfigSection
                key={opt.value}
                label={opt.label}
                reasons={reasonsConfig[opt.value] || []}
                onAdd={(val) => addReasonToConfig(opt.value, val)}
                onRemove={(idx) => removeReasonFromConfig(opt.value, idx)}
              />
            ))}
          </div>
          <div className="flex justify-end gap-2 pt-4 border-t shrink-0 mt-4">
            <Button variant="outline" onClick={() => setShowReasonsConfig(false)}>Cancel</Button>
            <Button onClick={saveReasonsConfig} disabled={reasonsConfigSaving}>
              {reasonsConfigSaving ? 'Saving...' : 'Save'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminRestrictions;
